import React, { useState, useRef, useMemo, useEffect } from "react";
import JoditEditor from "jodit-react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./AddBlogs.module.css";
import { useUpdateBlog, useBlogById } from "../../../hooks/admin/useBlogs";

function EditBlogs() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editor = useRef(null);
  const [blogData, setBlogData] = useState({
    title: "",
    slug: "",
    content: "",
    featured_image: null,
    tags: [],
    category: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch existing blog data
  const { data: existingBlog, isLoading: isFetching } = useBlogById(id);

  // Use the updateBlog mutation
  const { mutate: updateBlogPost, isLoading: isUpdating } = useUpdateBlog();

  // Set initial blog data when fetched
  useEffect(() => {
    if (existingBlog) {
      setBlogData({
        ...existingBlog,
        tags: existingBlog.tags || [],
        featured_image: null, // Reset file input
      });
      if (existingBlog.featured_image) {
        setImagePreview(existingBlog.featured_image);
      }
    }
  }, [existingBlog]);

  const joditConfig = useMemo(
    () => ({
      theme: "dark",
      height: 400,
      enableDragAndDropFileToEditor: true,
      showXPathInStatusbar: false,
      buttons: [
        "source",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "font",
        "fontsize",
        "brush",
        "paragraph",
        "|",
        "ul",
        "ol",
        "|",
        "table",
        "link",
        "|",
        "left",
        "center",
        "right",
        "justify",
        "|",
        "undo",
        "redo",
        "|",
        "hr",
        "eraser",
        "fullsize",
      ],
      uploader: {
        insertImageAsBase64URI: true,
      },
      colors: {
        background: "#222222",
        text: "#ffffff",
        defaultTextColor: "#ffffff",
        defaultBackgroundColor: "#222222",
      },
      style: {
        background: "#222222",
        color: "#ffffff",
      },
      statusbar: false,
      readonly: false,
      toolbarSticky: true,
      showPlaceholder: false,
      saveIndicator: false,
      maxHeight: 800,
      minHeight: 400,
      useSearch: false,
      spellcheck: false,
      editorCssClass: "dark-theme",
    }),
    []
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBlogData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setBlogData((prev) => ({
        ...prev,
        featured_image: file,
      }));
    }
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(",").map((tag) => tag.trim());
    setBlogData((prev) => ({
      ...prev,
      tags,
    }));
  };

  const handleEditorChange = (newContent) => {
    setBlogData((prev) => ({
      ...prev,
      content: newContent,
    }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (e) => {
    const { value } = e.target;
    setBlogData((prev) => ({
      ...prev,
      title: value,
      slug: generateSlug(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    updateBlogPost(
      { id, ...blogData },
      {
        onSuccess: () => {
          // Navigate to blogs list
          navigate("/admin/blogs");
        },
      }
    );
  };

  if (isFetching) {
    return <div className={styles.loading}>Loading blog data...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <h1 className={styles.title}>Edit Blog Post</h1>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={blogData.title}
              onChange={handleTitleChange}
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="slug">Slug</label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={blogData.slug}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Content</label>
            <div className={styles.joditWrapper}>
              <JoditEditor
                ref={editor}
                value={blogData.content}
                config={joditConfig}
                onChange={handleEditorChange}
                tabIndex={1}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label
              htmlFor="featured_image
"
            >
              Featured Image
            </label>
            <div className={styles.imageUploadContainer}>
              <input
                type="file"
                id="featured_image
"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                className={styles.fileInput}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className={styles.uploadButton}
              >
                Choose New Image
              </button>
              {imagePreview && (
                <div className={styles.imagePreviewContainer}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className={styles.imagePreview}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="tags">Tags (comma-separated)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={blogData.tags.join(", ")}
              onChange={handleTagsChange}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={blogData.category}
              onChange={handleInputChange}
            >
              <option value="">Select a category</option>
              <option value="Romantic">Romantic</option>
              <option value="Adventure">Adventure</option>
              <option value="Mystery">Mystery</option>
              <option value="Science Fiction">Science Fiction</option>
              <option value="Fantacy">Fantacy</option>
              <option value="Historical Fiction">Historical Fiction</option>
              <option value="Biography">Biography</option>
              <option value="Self-Help">Self-Help</option>
              <option value="Business">Business</option>
              <option value="Children">Children</option>
            </select>
          </div>

          <div className={styles.seoSection}>
            <h2>SEO Settings</h2>
            <div className={styles.inputGroup}>
              <label htmlFor="meta_title">Meta Title</label>
              <input
                type="text"
                id="meta_title"
                name="meta_title"
                value={blogData.meta_title}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="meta_description">Meta Description</label>
              <textarea
                id="meta_description"
                name="meta_description"
                value={blogData.meta_description}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="meta_keywords">Meta Keywords</label>
              <input
                type="text"
                id="meta_keywords"
                name="meta_keywords"
                value={blogData.meta_keywords}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Blog Post"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default EditBlogs;
